import * as B from '@blueprintjs/core';
import React from 'react';

import {ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';

import {RunProgressBarStatus} from './RunProgressBar';
import cs from './styles.module.scss';
import {AgentFeatureResult, ERROR_KIND_NO_IMAGERY} from './types';
import {sortFeatureResults} from './utils';

type Outcome = {
  label: string;
  icon?: B.IconName;
  intent?: B.Intent;
  isError?: boolean;
};

const describeOutcome = (result: AgentFeatureResult): Outcome => {
  switch (result.status) {
    case 'completed':
      return {label: 'Completed', icon: 'tick', intent: B.Intent.SUCCESS};
    case 'failed':
      return result.errorKind === ERROR_KIND_NO_IMAGERY
        ? {label: 'No imagery', icon: 'media'}
        : {label: 'Error', icon: 'error', isError: true};
    case 'cancelled':
      return {label: 'Cancelled'};
    default:
      return {label: 'In progress'};
  }
};

const ResultOutcome: React.FC<{result: AgentFeatureResult}> = ({result}) => {
  const {label, icon, intent, isError} = describeOutcome(result);
  return (
    <span className={isError ? cs.featureResultError : cs.featureResultOutcome}>
      {icon && <B.Icon icon={icon} size={12} intent={intent} />}
      {label}
    </span>
  );
};

// A property the run never settled gets a dash rather than a zero that reads as "looked and found
// nothing".
const FindingsCount: React.FC<{result: AgentFeatureResult}> = ({result}) => {
  if (result.findingsCount > 0) {
    return <span className={cs.findingsCount}>{result.findingsCount}</span>;
  }
  return (
    <span className={cs.featureResultOutcome}>{result.status === 'completed' ? '0' : '—'}</span>
  );
};

// The run row's dismissed/saved/remaining tags, for one property's observations.
const ReviewProgress: React.FC<{result: AgentFeatureResult}> = ({result}) => {
  if (result.findingsCount === 0) {
    return <span className={cs.featureResultOutcome}>—</span>;
  }
  return (
    <RunProgressBarStatus
      savedCount={result.savedCount}
      dismissedCount={result.dismissedCount}
      newCount={result.newCount}
      showLabels={false}
    />
  );
};

export const FeatureResultsTable: React.FC<{
  results: AgentFeatureResult[];
  areaUnit: ApiOrganizationAreaUnit;
  getPropertyHref: (result: AgentFeatureResult) => string | null;
}> = ({results, areaUnit, getPropertyHref}) => {
  const sorted = React.useMemo(() => sortFeatureResults(results), [results]);
  return (
    <div className={cs.tableContainer}>
      <table className={cs.table}>
        <thead>
          <tr>
            <th>Property</th>
            <th className={cs.featureResultsAreaCell}>
              Property {areaUnit === 'areaAcre' ? 'acres' : 'hectares'}
            </th>
            <th className={cs.featureResultsStatusCell}>Status</th>
            <th className={cs.featureResultsFindingsCell}>Findings</th>
            <th className={cs.featureResultsProgressCell}>Review progress</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((result) => {
            const propertyHref = getPropertyHref(result);
            return (
              <tr key={result.featureId}>
                <td>
                  <span className={cs.featureResultName}>
                    {result.featureName}
                    {propertyHref && (
                      <B.AnchorButton
                        minimal
                        small
                        intent="primary"
                        icon={<B.Icon icon="share" size={12} />}
                        href={propertyHref}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Open property in new tab"
                        aria-label={`Open ${result.featureName} in new tab`}
                      />
                    )}
                  </span>
                </td>
                <td className={cs.featureResultsAreaCell}>
                  {result.propertyArea ?? <span className={cs.featureResultOutcome}>—</span>}
                </td>
                <td className={cs.featureResultsStatusCell}>
                  <ResultOutcome result={result} />
                </td>
                <td className={cs.featureResultsFindingsCell}>
                  <FindingsCount result={result} />
                </td>
                <td className={cs.featureResultsProgressCell}>
                  <ReviewProgress result={result} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
